import { APP } from '@/common/strings'
import { Button } from '@/common/ui'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/common/ui/input-otp'
import { Onboarding } from '@/onboarding/layout/OnboardingPageTemplate'
import { autoSignIn, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth'
import { useEffect, useState, useRef } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { analytics } from '@/common/analytics/analytics.ts'
import { ONBOARDING_COPY, VERIFICATION_COPY } from '@/locales/en'
import { RefreshCw } from 'lucide-react'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'

export const ConfirmUser = () => {
    const [code, setCode] = useState('')
    const [verifying, setVerifying] = useState<boolean>(false)
    const [resendTimer, setResendTimer] = useState(60)
    const [resent, setResent] = useState(false)
    const [error, setError] = useState<string>('')
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()

    const userName = searchParams.get('username')

    const verifyUser = async () => {
        if (!userName) {
            setError('Error verifying user, Please try again!')
            navigate('/')
            return
        }
        setVerifying(true)
        setError('')
        try {
            const confirmedSignup = await confirmSignUp({ username: userName, confirmationCode: code })
            if (confirmedSignup.nextStep.signUpStep === 'COMPLETE_AUTO_SIGN_IN') {
                const { nextStep } = await autoSignIn()
                if (nextStep.signInStep === 'DONE') {
                    analytics.track(ANALYTICS_EVENTS.ONBOARDING.VERIFICATION_SUCCESS, {
                        verificationType: 'email',
                    })
                    navigate('/')
                }
            }
            if (confirmedSignup.nextStep.signUpStep === 'DONE') {
                analytics.track('onboarding_email_verification_succeeded')
                navigate('/')
            }
        } catch {
            analytics.track(ANALYTICS_EVENTS.ONBOARDING.VERIFICATION_FAILED, {
                verificationType: 'email',
            })
            setError('Incorrect code entered. Please check the code and try again.')
        } finally {
            setVerifying(false)
        }
    }

    const resendVerificationCode = async () => {
        if (resendTimer > 0) return
        // TODO: fix me, may be use Store, rather than taking username from route search params
        if (!userName) {
            setError('Error verifying user, Please try again!')
            navigate('/')
            return
        }
        setResent(true)
        setError('')
        try {
            await resendSignUpCode({ username: userName })
            analytics.track(ANALYTICS_EVENTS.ONBOARDING.VERIFICATION_RESENT, {
                verificationType: 'email',
            })
            setResendTimer(60)
        } catch {
            setError('Error resending code, Please try again!')
            analytics.track(ANALYTICS_EVENTS.ONBOARDING.VERIFICATION_RESENT_FAILED, {
                verificationType: 'email',
            })
            setResendTimer(60)
        } finally {
            setTimeout(() => setResent(false), 1000)
        }
    }

    const handleResendCode = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        resendVerificationCode()
    }

    // Countdown timer for resend
    useEffect(() => {
        if (resendTimer === 0) {
            if (timerRef.current) clearInterval(timerRef.current)
            return
        }
        timerRef.current = setInterval(() => {
            setResendTimer((t) => {
                if (t <= 1 && timerRef.current) {
                    clearInterval(timerRef.current)
                }
                return t > 0 ? t - 1 : 0
            })
        }, 1000)
        return () => {
            if (timerRef.current) clearInterval(timerRef.current)
        }
    }, [resendTimer])

    useEffect(() => {
        if (code.length === 6) {
            verifyUser()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code])

    if (!userName) {
        // TODO: or not coming from props
        // TODO: style it
        return <div>Invalid Confirmation link</div>
    }

    return (
        <>
            <Helmet>
                <title>
                    {ONBOARDING_COPY.confirmUser.pageTitle} | {APP.title}
                </title>
            </Helmet>
            <Onboarding
                title={ONBOARDING_COPY.confirmUser.title}
                description={
                    <>
                        {ONBOARDING_COPY.confirmUser.description}{' '}
                        <span style={{ whiteSpace: 'nowrap' }}>{userName}</span>
                    </>
                }
                cta={ONBOARDING_COPY.next}
                isLoading={verifying}
                showChatWidget={false}
                disabled={code.length < 6 || verifying}
                progress={17}
            >
                <div className="flex flex-col gap-4">
                    <div className="self-stretch inline-flex flex-col justify-start items-center gap-2">
                        <InputOTP maxLength={6} onChange={(value) => setCode(value)} value={code}>
                            <InputOTPGroup>
                                <InputOTPSlot index={0} />
                                <InputOTPSlot index={1} />
                                <InputOTPSlot index={2} />
                                <InputOTPSlot index={3} />
                                <InputOTPSlot index={4} />
                                <InputOTPSlot index={5} />
                            </InputOTPGroup>
                        </InputOTP>
                        {error && (
                            <div className="text-left md:text-center justify-start text-sentiment-bad text-sm font-normal font-['Inter'] leading-tight">
                                {error}
                            </div>
                        )}
                    </div>
                    <div className="w-full grid place-items-center gap-6">
                        <div className="self-stretch inline-flex justify-center items-center gap-6">
                            <Button
                                variant="ghost"
                                disabled={resendTimer > 0 || resent}
                                onClick={handleResendCode}
                                className={`h-10 px-6 py-2 rounded-[100px] flex justify-center items-center gap-2 transition-all duration-200 ${resent ? 'bg-green-100 text-green-700' : ''}`}
                            >
                                <RefreshCw strokeWidth="2.5px" className={`h-5 w-5${resent ? ' animate-spin' : ''}`} />
                                <div className="justify-start text-content-primary text-sm font-medium font-['Inter'] leading-tight">
                                    {resent
                                        ? VERIFICATION_COPY.sent
                                        : resendTimer > 0
                                          ? VERIFICATION_COPY.waiting.replace('{time}', resendTimer.toString())
                                          : VERIFICATION_COPY.resend}
                                </div>
                            </Button>
                        </div>
                    </div>
                </div>
            </Onboarding>
        </>
    )
}
